<template>
  <section class="login-layout">
    <div class="login-left">
      <div class="graphic-container">
        <img
          src="~@/assets/images/factoryAssembly.svg"
          alt="Manufacturing Illustration"
          class="main-graphic"
        />

        <div class="branding-text">
          <div class="branding-content">
            <h2>Manufacturing, Simplified.</h2>
            <!-- <h3>Plan. Produce. Track. Deliver.</h3> -->
            <p>
              One unified platform to streamline production, inventory, <br />
              quality, workflows, and more.
            </p>

            <div class="features-row">
              <div class="feature-item">
                <div class="icon-wrapper">
                  <SettingsIcon class="feature-icon" />
                </div>
                <span>Smart<br />Production</span>
              </div>
              <div class="feature-item">
                <div class="icon-wrapper">
                  <IntegrationIcon class="feature-icon" />
                </div>
                <span>Enterprise<br />Integration</span>
              </div>
              <div class="feature-item">
                <div class="icon-wrapper">
                  <CheckCircle class="feature-icon" />
                </div>
                <span>Quality<br />Control</span>
              </div>
              <div class="feature-item">
                <div class="icon-wrapper">
                  <WorkflowDashboardIcon class="feature-icon" />
                </div>
                <span>Real-time<br />Analytics</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="login-right">
      <div class="login-form-container">
        <div class="logo-wrapper">
          <img
            class="logo-img"
            src="~@/assets/images/dtx-logo-transparent.png"
            alt="DTX Manufacturing"
            height="150"
          />
        </div>
        <h1 class="welcome-text">Welcome back!</h1>
        <p class="subtitle">Please sign in to continue</p>

        <div class="login-form">
          <div data-validation="Email" class="input-group">
            <label for="email-input">Enter your email <span>*</span></label>
            <div class="input-wrapper">
              <i class="fa fa-envelope-o"></i>
              <input
                id="email-input"
                v-model="username"
                placeholder="Enter your email"
                type="text"
              />
            </div>
          </div>

          <div data-validation="Password" class="input-group">
            <label for="password-input">Enter your password <span>*</span></label>
            <div class="input-wrapper">
              <i class="fa fa-lock"></i>
              <input
                id="password-input"
                v-model="password"
                v-on:keyup.enter="tryLogin"
                placeholder="Enter your password"
                :type="passwordType"
              />
              <button
                type="button"
                class="password-toggle-btn"
                @click="passwordType = passwordType === 'password' ? 'text' : 'password'"
                :aria-label="passwordType === 'password' ? 'Show password' : 'Hide password'"
              >
                <img
                  v-if="passwordType === 'password'"
                  src="~@/assets/images/icons/open-eye.svg"
                  alt=""
                />
                <img
                  v-if="passwordType === 'text'"
                  src="~@/assets/images/icons/hide-eye.svg"
                  alt=""
                />
              </button>
            </div>
          </div>

          <div class="options-row">
            <!-- <div class="remember-row">
              <input v-model="remember_me" type="checkbox" id="remember_me" />
              <label class="pointer" for="remember_me"
                >Remember my password</label
              >
            </div> -->
            <div class="forgot-pwd-row">
              <router-link to="/forgot-password" class="pointer link">
                Forgot password?
              </router-link>
            </div>
          </div>

          <button @click="tryLogin" class="btn btn-login">Log In</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Cookie from '@/utils/cookie'
import UserLoginValidation from '@/helper/formValidation/userLogin'
import { getTokenStorage, login } from '@/api/session'
import Loader from '@/plugins/loader'
import CheckCircle from '@/assets/images/SVGcomponents/checkCircle.vue'
import SettingsIcon from '@/assets/images/SVGcomponents/Settings.vue'
import WorkflowDashboardIcon from '@/assets/images/SVGcomponents/WorkflowDashboardIcon.vue'
import IntegrationIcon from '@/assets/images/SVGcomponents/Integration.vue'

export default {
  name: 'loginPage',
  components: {
    CheckCircle,
    IntegrationIcon,
    SettingsIcon,
    WorkflowDashboardIcon
  },
  data () {
    return {
      username: '',
      password: '',
      remember_me: false,
      passwordType: 'password'
    }
  },
  methods: {
    checkRememberMe () {
      if (this.remember_me) {
        Cookie.write('UN', this.username)
        Cookie.write('UP', this.password)
      }
    },
    loginCallback (loginRes) {
      if (!loginRes.loggedIn) {
        this.$notify.alert(loginRes.message)
      } else {
        this.checkRememberMe()
        this.$notify.success(loginRes.message)
        const storage = getTokenStorage()
        const lastLocation = storage.getItem('lastPath') || '/'
        storage.setItem('lastPath', '')
        window.history.replaceState({}, '', '/')
        window.location.href = lastLocation
      }
    },
    tryLogin (event) {
      if (event.type === 'keypress' && event.code !== 'Enter') {
        return
      }
      const loader = new Loader()
      if (UserLoginValidation(this.username, this.password)) {
        loader.show()
        login(this.username, this.password).then(res => {
          this.loginCallback(res)
        }).catch(err => {
          this.$notify.alert(err?.message ?? 'Something went wrong')
        }).finally(() => {
          loader.hide()
        })
      }
    }
  },
  created () {
    this.username = Cookie.read('UN')
    this.password = Cookie.read('UP')
    document.body.addEventListener('keypress', this.tryLogin)
  },
  destroyed () {
    document.body.removeEventListener('keypress', this.tryLogin)
  }
}
</script>

<style lang="scss" scoped>
@keyframes slideFadeInLeft {
  0% {
    opacity: 0;
    transform: translateX(-40px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideFadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes floatAnimation {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0);
  }
}

.login-layout {
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  font-family: "Nunito Sans", sans-serif;
  background:
    url("~@/assets/images/SideCurl.svg") calc(54.55vw - 47.25vh) center / auto 100% no-repeat,
    linear-gradient(to right, #ffffff 54.55%, #f8f2e8 80%);
}

.login-left {
  flex: 1.2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  padding: 2rem 0 0 5rem;
  position: relative;

  .graphic-container {
    width: 106%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    justify-content: center;
  }

  .main-graphic {
    width: 100%;
    max-height: 70vh;
    object-fit: contain;
    margin-bottom: 8rem;
    z-index: 1;
    animation: floatAnimation 6s ease-in-out infinite;
  }

  .branding-text {
    position: absolute;
    bottom: -3%;
    left: -2%;
    width: 85%;
    text-align: left;
    animation: slideFadeInLeft 0.9s ease-out forwards;
    background: linear-gradient(
      to top,
      rgba(255, 255, 255, 1) 25%,
      rgba(255, 255, 255, 0.95) 45%,
      rgba(255, 255, 255, 0.6) 70%,
      rgba(255, 255, 255, 0.3) 90%,
      rgba(255, 255, 255, 0) 100%
    );
    padding: 5rem 0 4rem 0;
    box-sizing: border-box;
    z-index: 10;
    display: flex;
    justify-content: flex-start;

    .branding-content {
      width: 100%;
      max-width: 650px;
      padding: 0 0 0 4rem;

      h2 {
        font-size: 2.2rem;
        font-weight: 600;
        color: #333;
        margin: 0 0 0.8rem 0;
      }

      h3 {
        font-size: 1.3rem;
        color: #b5985a;
        margin: 0 0 1.2rem 0;
        font-weight: 600;
      }

      p {
        color: #666;
        font-size: 0.95rem;
        margin: 0 0 1rem 0;
        line-height: 1.5;
        font-weight: 500;
        max-width: 550px;
      }

      .features-row {
        display: flex;
        justify-content: flex-start;
        gap: 1.5rem;
        width: 100%;

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.6rem;

          .icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 38px;
            height: 38px;
            border: 2px solid #e5d8b8;
            border-radius: 10px;
            background-color: transparent;
          }

          .feature-icon {
            width: 20px;
            height: 20px;
            opacity: 0.8;
            color: #b5985a;
          }

          span {
            font-size: 0.85rem;
            color: #555;
            line-height: 1.2;
            font-weight: 500;
          }
        }
      }
    }
  }
}

.login-right {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  .login-form-container {
    width: 70%;
    max-width: 450px;
    display: flex;
    flex-direction: column;
    padding-left: 2rem;
    animation: slideFadeInUp 0.8s ease-out forwards;
  }

  .logo-wrapper {
    margin-bottom: -0.8rem;
    display: flex;
    justify-content: center;
  }

  .welcome-text {
    margin: 0;
    color: #34383d;
    font-size: 27px;
    line-height: 36px;
    font-weight: 600;
    text-align: center;
  }

  .subtitle {
    margin: 8px 0 26px;
    color: #6f7378;
    font-size: 14px;
    line-height: 22px;
    text-align: center;
  }

  .login-form {
    width: 100%;
    text-align: left;

    .input-group {
      width: 100%;
      margin-bottom: 18px;

      label {
        display: block;
        margin-bottom: 7px;
        color: #45484c;
        font-size: 13px;
        font-weight: 500;

        span {
          color: #c7983a;
        }
      }

      .input-wrapper {
        position: relative;
        width: 100%;

        i {
          position: absolute;
          top: 50%;
          left: 13px;
          transform: translateY(-50%);
          color: #8b8f93;
          font-size: 14px;
        }

        input {
          width: 100%;
          height: 37px;
          box-sizing: border-box;
          padding: 0 12px 0 39px;
          border: 1px solid #dedede;
          border-radius: 4px;
          outline: none;
          color: #444;
          background: #fff;
          font-size: 13px;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;

          &::placeholder {
            color: #9b9da0;
          }

          &:focus {
            border-color: #c79a3c;
            box-shadow: 0 0 0 2px rgba(199, 154, 60, 0.08);
          }
        }

        .password-toggle-btn {
          position: absolute;
          top: 50%;
          right: 12px;
          padding: 0;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          cursor: pointer;
          opacity: 0.7;
          display: flex;
          align-items: center;
          justify-content: center;

          &:focus-visible {
            outline: 2px solid #c79a3c;
            outline-offset: 2px;
            border-radius: 2px;
          }

          img {
            width: 14px;
            height: 14px;
            display: block;
          }
        }
      }
    }

    .options-row {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-bottom: 24px;
      margin-top: -8px;
    }

    .forgot-pwd-row {
      .link {
        color: #4e3fbc;
        font-size: 13px;
        text-decoration: underline;

        &:hover {
          color: #362985;
        }
      }
    }

    .remember-row {
      display: flex;
      align-items: center;

      input[type="checkbox"] {
        width: 14px;
        height: 14px;
        margin: 0;
        cursor: pointer;
        accent-color: #c7983a;
      }

      label {
        margin: 0 0 0 8px;
        color: #45484c;
        font-size: 13px;
        cursor: pointer;
      }
    }

    .btn-login {
      width: 100%;
      height: 38px;
      margin-top: 1px;
      border: 0;
      border-radius: 4px;
      background: #ba9952;
      color: #ffffff;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.2s ease, transform 0.1s ease;

      &:hover {
        background: #a48139;
      }

      &:active {
        transform: translateY(1px);
      }
    }
  }
}

@media (min-width: 1920px) {
  .login-left .branding-text {
    padding: 2rem 0 4rem 0;
    .branding-content {
      max-width: 900px;
      padding-left: 6rem;

      h2 {
        font-size: 2.2rem;
        margin-bottom: 0.9rem;
      }
      h3 {
        font-size: 1rem;
        margin-bottom: 1.1rem;
      }
      p {
        font-size: 0.85rem;
        margin-bottom: 2rem;
        max-width: 700px;
      }

      .features-row {
        gap: 1.5rem;
        .feature-item {
          gap: 0.8rem;
          .icon-wrapper {
            width: 48px;
            height: 48px;
            border-width: 2px;
            border-radius: 12px;
          }
          .feature-icon {
            width: 24px;
            height: 24px;
          }
          span {
            font-size: 0.85rem;
          }
        }
      }
    }
  }
}

@media (max-width: 1199px), (max-height: 650px) {
  .login-left .branding-text {
    padding: 3rem 0 2rem 0;

    .branding-content {
      max-width: 560px;
      padding-left: 3rem;

      h2 {
        font-size: 1.8rem;
        margin-bottom: 0.5rem;
      }
      h3 {
        font-size: 1.2rem;
        margin-bottom: 0.7rem;
      }
      p {
        font-size: 0.9rem;
        margin-bottom: 1.3rem;
        line-height: 1.3;
      }

      .features-row {
        gap: 0.9rem;

        .feature-item {
          gap: 0.5rem;

          .icon-wrapper {
            width: 26px;
            height: 26px;
            border-width: 1.5px;
            border-radius: 6px;
          }

          .feature-icon {
            width: 14px;
            height: 14px;
          }

          span {
            font-size: 0.7rem;
            line-height: 1.1;
          }
        }
      }
    }
  }
}

@media (max-width: 992px) {
  .login-layout {
    background: #faf8f5;
  }

  .login-left {
    display: none;
  }

  .login-right {
    padding: 2rem 1rem;

    .login-form-container {
      padding-left: 0;
      max-width: 400px;
      margin: 0 auto;
    }
  }
}

</style>
